import { useErrorAlert } from "@/shared/lib/useErrorAlert";
import planApi from "../api/plan.api";
import { MultyPlanInterface, MultyPlanResultInterface, PlanInterface, PlanResponse, PlanTypeInterface } from "../model/plan.types";
import { usePlan } from "./usePlan"
import planTypeApi from "../api/planType.api";
import { CreatePlanPopupForm } from "@/features/Sale/HeaderDashboard/components/_popups/CreatePlanPopup/CreatePlanPopup.model";
import { useNotificationActions } from "@/features/Notification/lib/useNotificationActions";
import { handleApiResponse } from "@/shared/lib/api";
import { EditCompanysPlanFormInterface, EditPlanFormInterface } from "@/features/Sale/HeaderDashboard/components/_popups/EditPlanPopup/EditPlanPopup.model";
import { CreateCompanysPlanForm } from "@/features/Sale/HeaderDashboard/components/_popups/CreateCompanysPlanPopup/CreateCompanysPlanPopup.model";

const usePlanActions = () => {
  const { setLoadingState, setErrorState, setMyPlans, setPlanTypes, setMyMultyPlans} = usePlan()
  const { showErrorAlert } = useErrorAlert()
  const { topAlert } = useNotificationActions()

  const getMyPlans = async (): Promise<PlanInterface[] | null> => {
    setLoadingState("myPlans", true);
    setErrorState("myPlans", null);
    try {
      const res = await planApi.getMyPlans();
      const result = handleApiResponse<PlanInterface[]>(res, { showErrorAlert, topAlert });
      if (result) setMyPlans(result);
      return result;
    } catch (error: any) {
      setErrorState("myPlans", error as string);
      const errorMessage = error?.response?.data?.error_message;
      showErrorAlert(errorMessage || error);
      return null;
    } finally {
      setLoadingState("myPlans", false);
    }
  };

  const getMyMyltyPlans = async (): Promise<MultyPlanInterface[]> => {
    setLoadingState("myMultyPlans", true);
    setErrorState("myMultyPlans", null);
    try {
      const res = await planApi.getMyMultyPlans();
      const result = handleApiResponse<MultyPlanInterface[]>(res, { showErrorAlert, topAlert }) ?? [];
      if (result) setMyMultyPlans(result);
      return result;
    } catch (error: any) {
      setErrorState("myMultyPlans", error as string);
      const errorMessage = error?.response?.data?.error_message;
      showErrorAlert(errorMessage || error);
      return [];
    } finally {
      setLoadingState("myMultyPlans", false);
    }
  };

  const getPlanTypes = async (): Promise<PlanTypeInterface[] | null> => {
    setLoadingState("planTypes", true);
    setErrorState("planTypes", null);
    try {
      const res = await planTypeApi.getPlanTypes();
      const result = handleApiResponse<PlanTypeInterface[]>(res, { showErrorAlert, topAlert });
      if (result) setPlanTypes(result);
      return result;
    } catch (error: any) {
      setErrorState("planTypes", error as string);
      const errorMessage = error?.response?.data?.error_message;
      showErrorAlert(errorMessage || error);
      return null;
    } finally {
      setLoadingState("planTypes", false);
    }
  };
  
  const createPlan = async (data: CreatePlanPopupForm): Promise<PlanInterface | null> => {
    setLoadingState("createPlan", true);
    setErrorState("createPlan", null);
    try {
      const res = await planApi.createPlan(data);
      const result = handleApiResponse<PlanInterface>(res, { showErrorAlert, topAlert });
      if (result) getMyPlans();
      return result;
    } catch (error: any) {
      setErrorState("createPlan", error as string);
      const errorMessage = error?.response?.data?.error_message;
      showErrorAlert(errorMessage || error);
      return null;
    } finally {
      setLoadingState("createPlan", false);
    }
  };

  const createMyltyPlan = async (data: CreateCompanysPlanForm): Promise<MultyPlanResultInterface | null> => {
    setLoadingState("createMultyPlan", true);
    setErrorState("createMultyPlan", null);
    try {
      const res = await planApi.createMyltyPlan(data);
      const result = handleApiResponse<any>(res, { showErrorAlert, topAlert });
      if (result) {getMyPlans(); getMyMyltyPlans()};
      return result;
    } catch (error: any) {
      setErrorState("createMultyPlan", error as string);
      const errorMessage = error?.response?.data?.error_message;
      showErrorAlert(errorMessage || error);
      return null;
    } finally {
      setLoadingState("createMultyPlan", false);
    }
  };

  const update = async (data: EditPlanFormInterface): Promise<PlanInterface | null> => {
    setLoadingState("update", true);
    setErrorState("update", null);
    try {
      const res = await planApi.update(data);
      const result = handleApiResponse<PlanInterface>(res, { showErrorAlert, topAlert });
      if (result) getMyPlans();
      return result;
    } catch (error: any) {
      setErrorState("update", error as string);
      const errorMessage = error?.response?.data?.error_message;
      showErrorAlert(errorMessage || error);
      return null;
    } finally {
      setLoadingState("update", false);
    }
  }

  const deletePlan = async (id: number): Promise<PlanInterface | null> => {
    setLoadingState("delete", true);
    setErrorState("delete", null);
    try {
      const res = await planApi.delete(id);
      const result = handleApiResponse<PlanInterface>(res, { showErrorAlert, topAlert });
      if (result) getMyPlans();
      return result;
    } catch (error: any) {
      setErrorState("delete", error as string);
      const errorMessage = error?.response?.data?.error_message;
      showErrorAlert(errorMessage || error);
      return null;
    } finally {
      setLoadingState("delete", false);
    }
  }

  const deleteMulty = async (id: number): Promise<MultyPlanResultInterface | null> => {
    setLoadingState("delete", true);
    setErrorState("delete", null);
    try {
      const res = await planApi.delete(id);
      const result = handleApiResponse<MultyPlanResultInterface>(res, { showErrorAlert, topAlert });
      if (result) getMyPlans();
      return result;
    } catch (error: any) {
      setErrorState("delete", error as string);
      const errorMessage = error?.response?.data?.error_message;
      showErrorAlert(errorMessage || error);
      return null;
    } finally {
      setLoadingState("delete", false);
    }
  }

  const getResultUserPlans = async (data: {dateStart: string, dateEnd: string}, user_id: number): Promise<PlanResponse | null> => {
    setLoadingState("getResultUserPlans", true);
    setErrorState("getResultUserPlans", null);
    try {
      if (!data.dateStart || !data.dateEnd) return null;
      const res = await planApi.getResultUserPlans({dateStart: data.dateStart, dateEnd: data.dateEnd}, user_id);
      const result = handleApiResponse<PlanResponse>(res, { showErrorAlert, topAlert });
      return result;
    } catch (error: any) {
      setErrorState("getResultUserPlans", error as string);
      const errorMessage = error?.response?.data?.error_message;
      showErrorAlert(errorMessage || error);
      return null;
    } finally {
      setLoadingState("getResultUserPlans", false);
    }
  }

  const updateMultyplan = async (data: EditCompanysPlanFormInterface): Promise<MultyPlanResultInterface | null> => {
    setLoadingState("update", true);
    setErrorState("update", null);
    try {
      const res = await planApi.updateMyltyPlan(data);
      const result = handleApiResponse<MultyPlanResultInterface>(res, { showErrorAlert, topAlert }) ?? null;
      if (result) getMyMyltyPlans();
      return result;
    } catch (error: any) {
      setErrorState("update", error as string);
      const errorMessage = error?.response?.data?.error_message;
      showErrorAlert(errorMessage || error);
      return null;
    } finally {
      setLoadingState("update", false);
    }
  }

  
  return { 
    getResultUserPlans,
    updateMultyplan,
    getMyMyltyPlans,
    createMyltyPlan,
    getPlanTypes, 
    getMyPlans, 
    createPlan, 
    deleteMulty,
    deletePlan, 
    update, 
  }
}

export { usePlanActions }